import { useLocation, Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

/**
 * Sticky early-access disclaimer.
 *
 * Founder rule (updated 2026-05): until SonSpeak is lawyer-cleared and
 * `REACT_APP_DEMO_MODE=false` is set in production env, this banner MUST be
 * visible on every page, every load, with no dismiss button. The wording
 * flipped from "Demo only" to "Early access" once real signups + paid
 * intent started landing — "demo" was a legal contradiction with a checkout
 * page, "early access" honestly describes the product state.
 *
 * The medical/clinical/emergency disclaimer is unchanged — that's the
 * legally protective copy, not the word "demo".
 *
 * The kill-switch is still the env var, not a click. Set REACT_APP_DEMO_MODE=
 * false in the frontend env after lawyer clearance and this banner disappears
 * everywhere instantly.
 *
 * Internal names (data-testid="demo-banner", REACT_APP_DEMO_MODE, isDemoMode(),
 * /demo-safety route, file name) are intentionally preserved so the existing
 * test suite, sitemap, and inbound bookmarks all keep working.
 */

/**
 * Early-access flag — drives this banner AND the early-access pills on
 * registration, prefill helpers in the auth forms, and other "this is a
 * preview" affordances. All gated by the same isDemoMode() so a single env
 * flip disables them together. Function name kept as isDemoMode() for
 * backwards-compat — the user-visible copy is what changed.
 */
export function isDemoMode() {
  const v = (process.env.REACT_APP_DEMO_MODE ?? "true").toString().toLowerCase();
  return v !== "false" && v !== "0" && v !== "no";
}

// Routes where the parent/caregiver is mid-task on a single high-focus
// transactional flow. On these, the early-access disclaimer collapses to a
// small badge so it doesn't compete for attention with the form. Clicking
// the badge re-expands the full message.
//
// We deliberately keep the disclaimer visible (NOT hidden) — the legal
// "not a medical/emergency tool" notice still needs to be one click away on
// every page. Collapsed badge satisfies that while restoring focus.
const FOCUS_ROUTES = [
  "/consent/parental/verify",
];

function isFocusRoute(pathname) {
  return FOCUS_ROUTES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export default function DemoBanner() {
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  if (!isDemoMode()) return null;
  const focus = isFocusRoute(location.pathname);

  // Full / default behavior on every page except focus routes.
  if (!focus || expanded) {
    return (
      <div
        role="alert"
        aria-live="polite"
        data-testid="demo-banner"
        className="sticky top-0 z-[100] w-full bg-[color:var(--coral)] text-white border-b-[3px] border-[color:var(--ink)] shadow-[0_3px_0_0_var(--ink)]"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3">
          <AlertTriangle size={18} className="shrink-0" aria-hidden />
          <p className="text-sm sm:text-[15px] leading-snug font-semibold flex-1 min-w-0">
            <span className="font-extrabold uppercase tracking-wider mr-1">Early access.</span>
            SonSpeak is not a medical, clinical, or emergency tool. For real-world
            safety or emergencies, contact your doctor or 911.{" "}
            <Link
              to="/demo-safety"
              className="underline font-bold"
              data-testid="demo-banner-learn"
            >
              Learn more →
            </Link>
          </p>
          {focus && expanded && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="shrink-0 inline-flex items-center gap-1 text-xs underline font-bold opacity-80 hover:opacity-100"
              aria-label="Collapse banner"
              data-testid="demo-banner-collapse"
            >
              <ChevronUp size={14} /> Hide
            </button>
          )}
        </div>
      </div>
    );
  }

  // Collapsed badge — focus route, default collapsed state.
  // Single-line, no card chrome, keeps an "Early access" pill + "Learn more"
  // affordance so the legal disclosure is still one click away on every render.
  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="demo-banner"
      data-state="collapsed"
      className="sticky top-0 z-[100] w-full bg-[color:var(--cream)] border-b border-[color:var(--paper-muted,#E5E1D8)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-1.5 flex items-center justify-between gap-2 text-xs">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1.5 font-bold uppercase tracking-wider text-[color:var(--coral-deep,#C92A36)] hover:underline"
          aria-label="Expand safety notice"
          data-testid="demo-banner-expand"
        >
          <AlertTriangle size={12} /> Early access
          <ChevronDown size={12} />
        </button>
        <Link
          to="/demo-safety"
          className="underline font-semibold text-[color:var(--ink-soft)]"
          data-testid="demo-banner-learn"
        >
          Safety notice
        </Link>
      </div>
    </div>
  );
}
